import type { RouteSpecs } from '@rs/spec';
import { noop } from '@rs/utils';
import { HttpClientError } from './errors';
import type { HttpClient, HttpClientOptions } from './types';

// Бэк никогда не отдаёт 403, поэтому 403 на API = upstream-блокировка
// (Cloudflare challenge / WAF / etc). Полный reload документа заставит
// браузер получить challenge HTML и пройти его, после чего follow-up
// API запросы пройдут. Guard на 30s защищает от цикла, если блокировка
// не снимается reload'ом.
const RELOAD_403_GUARD_KEY = 'ps_403_reload_at';
const RELOAD_403_GUARD_MS = 30_000;

function tryReloadOn403(status: number): boolean {
	if (status !== 403) {
		return false;
	}

	if (!('window' in globalThis) || !('sessionStorage' in globalThis)) {
		return false;
	}

	const lastReloadAt = Number(globalThis.sessionStorage.getItem(RELOAD_403_GUARD_KEY) ?? 0);

	if (Date.now() - lastReloadAt < RELOAD_403_GUARD_MS) {
		return false;
	}

	globalThis.sessionStorage.setItem(RELOAD_403_GUARD_KEY, String(Date.now()));
	globalThis.location.reload();
	return true;
}

type CallOptions = {
	headers?: Record<string, string>;
	params?: Record<string, string>;
	query?: Record<string, boolean | number | string>;
};

function buildUrl(path: string, baseUrl: string, callOptions: CallOptions | undefined): URL {
	const url = new URL(path, baseUrl);

	if (callOptions?.params) {
		for (const [key, value] of Object.entries(callOptions.params)) {
			url.pathname = url.pathname.replace(`:${key}`, encodeURIComponent(value));
		}
	}

	if (callOptions?.query) {
		for (const [key, value] of Object.entries(callOptions.query)) {
			url.searchParams.set(key, String(value));
		}
	}

	return url;
}

function buildHeaders(
	method: string,
	defaultHeaders: HttpClientOptions['defaultHeaders'],
	callOptions: CallOptions | undefined,
): Record<string, string> {
	const headers: Record<string, string> = {};

	const defaults = typeof defaultHeaders === 'function' ? defaultHeaders() : defaultHeaders;

	if (defaults) {
		Object.assign(headers, defaults);
	}

	if (callOptions?.headers) {
		Object.assign(headers, callOptions.headers);
	}

	if (method !== 'GET') {
		headers['content-type'] = 'application/json';
	}

	return headers;
}

export function createHttpClient<T extends RouteSpecs>(routes: T, options: HttpClientOptions): HttpClient<T> {
	const { baseUrl, defaultHeaders, onError, reloadOn403 } = options;
	const client: Record<string, (...args: unknown[]) => Promise<unknown>> = {};

	for (const [name, spec] of Object.entries(routes)) {
		client[name] = async (...args: unknown[]) => {
			let body: unknown;
			let callOptions: CallOptions | undefined;

			if ('body' in spec) {
				body = args[0];
				callOptions = args[1] as CallOptions | undefined;
			} else {
				callOptions = args[0] as CallOptions | undefined;
			}

			const url = buildUrl(spec.path, baseUrl, callOptions);
			const headers = buildHeaders(spec.method, defaultHeaders, callOptions);

			const init: RequestInit = {
				method: spec.method,
				headers,
				...(spec.method !== 'GET' && { body: JSON.stringify(body ?? {}) }),
			};

			const response = await fetch(url.href, init);

			if (!response.ok) {
				if (reloadOn403 && tryReloadOn403(response.status)) {
					// Навигация уходит — промис не зарезолвится, чтобы caller не успел показать ошибку.
					await new Promise<never>(noop);
				}

				const text = await response.text();
				const error = new HttpClientError(spec.method, spec.path, response.status, text);
				onError?.(error);
				throw error;
			}

			return response.json() as Promise<unknown>;
		};
	}

	return client as HttpClient<T>;
}
